import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import noImage from "../../../../public/noImage.webp";

import classes from "./BookingPhoneContent.module.css";
import BookingDetailsMain from "../BookingDetailsMain";
import BookingDetailsCard from "../../../../pages/BookingsPage/BookingDetails/BookingDetailsCard";
import { markBookingAsViewed } from "../../../../util/bookingViewedTracker";
import { formatBerlinDateTimeRange, isCompletedBookingStatus } from "../../../../util/bookingDateTime";
import { buildCoarseLocationText, buildExactLocationText, canShowExactLocationForStatus } from "../../../../util/locationPrivacy";
import { notifyErrorToast, notifyValidationToast } from '../../../../util/notifyActionError';

export const BookingPhoneContent = ({ bookingData, packageData = null, fromCreatorPage = false }) => { 
    const { user } = useSelector((state) => state?.auth);
    const userId = user?.id || user?._id;
    
    const [isExpanded, setIsExpanded] = useState(false);
    const [isPriceExpanded, setIsPriceExpanded] = useState(false);
    // const [isBookingExpanded, setIsBookingExpanded] = useState(false);
    const [text, setText] = useState('');
    const [showExpandButton, setShowExpandButton] = useState(false);
    const isHostViewer = bookingData?.studioOwnerID === userId;
    const hasExactLocation = (isHostViewer || canShowExactLocationForStatus(bookingData?.status)) && !!bookingData?.exactLocation;
    const exactAddressText = buildExactLocationText(bookingData?.exactLocation || {});
    const coarseAddressText = buildCoarseLocationText(bookingData?.Studio) || bookingData?.Studio?.city || '';
    const addressText = hasExactLocation && exactAddressText
        ? exactAddressText
        : coarseAddressText;

    // Check if booking start time has passed
    const hasBookingTimePassed = () => {
        if (!bookingData?.startDateTime) return false;
        const bookingStartTime = new Date(bookingData.startDateTime);
        const now = new Date();
        return now > bookingStartTime;
    };

    // Mark booking as viewed when component mounts or booking data changes
    useEffect(() => {
        if (bookingData?.id && bookingData?.status && userId) {
            markBookingAsViewed(bookingData.id, bookingData.status, userId);
        }
    }, [bookingData?.id, bookingData?.status, userId]);

    // Calculate booking duration in hours (decimal)
    const calculateBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return 0;
        const start = new Date(startDateTime);
        const end = new Date(endDateTime);
        const durationMs = end.getTime() - start.getTime();
        const durationHours = durationMs / (1000 * 60 * 60);
        return Math.round(durationHours * 100) / 100; // Round to 2 decimal places
    };

    // Format duration for display (e.g., "2 Stunden")
    const formatBookingDuration = (startDateTime, endDateTime) => {
        if (!startDateTime || !endDateTime) return "0 Stunden";
        const duration = calculateBookingDuration(startDateTime, endDateTime);
        const hours = Math.round(duration); // Round to nearest hour since only hours are used
        return `${hours} Stunden`;
    };

    const appliedFees = Array.isArray(bookingData?.appliedFees) ? bookingData.appliedFees : [];

    useEffect(() => {
        if (!bookingData) {
            setText('');
            setShowExpandButton(false);
            return;
        }

        const fullText = bookingData.projectDescription || '';
        const shouldShowExpand = fullText.length > 150;
        setShowExpandButton(shouldShowExpand);
        setText(shouldShowExpand ? (isExpanded ? fullText : `${fullText.slice(0, 150)}...`) : fullText);
    }, [isExpanded, bookingData?.projectDescription]);

    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    const togglePriceExpand = () => {
        setIsPriceExpanded(!isPriceExpanded);
    };   
    
    const formatDateTime = (startDate, endDate) => {
        return formatBerlinDateTimeRange(startDate, endDate) || {
            date: 'Datum nicht verfügbar',
            time: 'Zeit nicht verfügbar'
        };
    };

    const calculateDuration = (start, end) => {
        const startTime = new Date(start);
        const endTime = new Date(end);
        const durationHours = (endTime - startTime) / (1000 * 60 * 60);
        return durationHours;
    };

    const calculateHoursRemaining = (createdAt) => {
        if (!createdAt) return null;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        const hoursRemaining = Math.max(0, Math.ceil(24 - hoursPassed));
        return hoursRemaining;
    };

    const isBookingExpired = (createdAt) => {
        if (!createdAt) return false;
        const created = new Date(createdAt);
        const now = new Date();
        const hoursPassed = (now - created) / (1000 * 60 * 60);
        return hoursPassed > 24;
    };

    const getStatusDisplay = () => {
        const status = bookingData?.status;
        const hostName = bookingData?.Studio?.User?.firstName || '';
        const guestName = bookingData?.User?.firstName || '';
        const cancelledBy = bookingData?.cancelledBy;
        const createdAt = bookingData?.createdAt;
        const hoursRemaining = calculateHoursRemaining(createdAt);
        const expired = isBookingExpired(createdAt);
        const completed = isCompletedBookingStatus(status);

        // Check if cancelled by guest or host
        // cancelledBy is an enum: "HOST" | "GUEST" | "SYSTEM"
        const cancelledByGuest = cancelledBy === "GUEST";
        const cancelledByHost = cancelledBy === "HOST";
        const cancelledBySystem = cancelledBy === "SYSTEM";

        if (completed) {
            return {
                message: '',
                statusText: 'Abgeschlossen',
                color: '#A1A7AE'
            };
        }

        if (status === "NO_SHOW") {
            return {
                message: fromCreatorPage 
                    ? `${guestName} ist nicht erschienen`
                    : 'Du bist nicht erschienen',
                statusText: 'Nicht erschienen',
                color: '#CE425D'
            };
        }

        if (status === "EXPIRED" || (expired && status === "PENDING")) {
            return {
                message: 'Die Anfrage ist abgelaufen',
                statusText: 'Anfrage abgelaufen',
                color: '#CE425D'
            };
        }

        if (status === "INQUIRY" || (status === "PENDING" && !expired && hoursRemaining === null)) {
            return {
                message: fromCreatorPage 
                    ? `Erkundigung von ${guestName}`
                    : `Du hast dich bei ${hostName} erkundigt`,
                statusText: 'Erkundigung',
                color: '#2788EE'
            };
        }

        if (status === "PENDING" && !expired) {
            return {
                message: fromCreatorPage 
                    ? `${guestName} hat bei dir angefragt`
                    : `Du hast bei ${hostName} angefragt`,
                statusText: `Angefragt: Läuft in ${hoursRemaining} ${hoursRemaining === 1 ? 'Stunde' : 'Stunden'} ab`,
                color: '#F7871E'
            };
        }

        if (status === "WITHDRAWN") {
            return {
                message: fromCreatorPage 
                    ? `${guestName} hat die Anfrage zurückgezogen`
                    : `Du hast die Anfrage zurückgezogen`,
                statusText: 'Anfrage zurückgezogen',
                color: '#CE425D'
            };
        }

        if (status === "DECLINED" || status === "REJECTED") {
            return {
                message: fromCreatorPage 
                    ? `Du hast die Buchungsanfrage abgelehnt`
                    : `${hostName} hat die Buchungsanfrage abgelehnt`,
                statusText: 'Anfrage abgelehnt',
                color: '#CE425D'
            };
        }

        if (status === "CONFIRMED") {
            return {
                message: fromCreatorPage 
                    ? `Du hast die Buchung bestätigt`
                    : `${hostName} hat die Buchungsanfrage bestätigt`,
                statusText: 'Buchung bestätigt',
                color: '#20BD13'
            };
        }

        if (status === "CANCELLED") {
            if (fromCreatorPage) {
                // Creator mode: Case 6 & 7
                if (cancelledByGuest) {
                    // Guest cancelled: "[Name des Entdeckers] hat die Buchung storniert" Status: "Vom Entdecker storniert"
                    return {
                        message: `${guestName} hat die Buchung storniert`,
                        statusText: 'Vom Entdecker storniert',
                        color: '#CE425D'
                    };
                } else if (cancelledByHost) {
                    // Creator cancelled: "Du hast die Buchung storniert" Status: "Vom Creator storniert"
                    return {
                        message: `Du hast die Buchung storniert`,
                        statusText: 'Vom Creator storniert',
                        color: '#CE425D'
                    };
                } else if (cancelledBySystem) {
                    // System cancelled
                    return {
                        message: 'Die Buchung wurde automatisch storniert',
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                } else {
                    // Fallback if cancelledBy is not available - assume creator cancelled
                    return {
                        message: `Du hast die Buchung storniert`,
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                }
            } else {
                // Entdecker mode: Case 6 & 7
                if (cancelledByHost) {
                    // Host cancelled: "[Name des Hosts] hat die Buchung storniert" Status: "Von [Name des Hosts] storniert"
                    return {
                        message: `${hostName} hat die Buchung storniert`,
                        statusText: `Von ${hostName} storniert`,
                        color: '#CE425D'
                    };
                } else if (cancelledByGuest) {
                    // Guest cancelled: "Du hast die Buchung storniert" Status: "Von dir storniert"
                    return {
                        message: `Du hast die Buchung storniert`,
                        statusText: 'Von dir storniert',
                        color: '#CE425D'
                    };
                } else if (cancelledBySystem) {
                    // System cancelled
                    return {
                        message: 'Die Buchung wurde automatisch storniert',
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                } else {
                    // Fallback if cancelledBy is not available - assume host cancelled
                    return {
                        message: `${hostName} hat die Buchung storniert`,
                        statusText: 'Storniert',
                        color: '#CE425D'
                    };
                }
            }
        }

        // Default fallback
        return {
            message: '',
            statusText: 'Unbekannt',
            color: '#A1A7AE'
        };
    };

    const generateICSFile = () => {
        // Validate that we have the required data
        if (!bookingData?.startDateTime || !bookingData?.endDateTime) {
            notifyValidationToast('Datum und Zeit der Buchung sind nicht verfügbar.');
            return;
        }

        try {
            // Parse the dates directly as Date objects (same approach as formatDateTime)
            const startDate = new Date(bookingData.startDateTime);
            const endDate = new Date(bookingData.endDateTime);

            // Validate that the dates are valid
            if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
                notifyValidationToast('Ungültiges Datum oder Zeit in der Buchung.');
                return;
            }

            const formatDateForICS = (date) => {
                return date.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
            };

            const icsContent = `BEGIN:VCALENDAR
                VERSION:2.0
                BEGIN:VEVENT
                DTSTART:${formatDateForICS(startDate)}
                DTEND:${formatDateForICS(endDate)}
                SUMMARY:Termin bei ${bookingData?.User?.firstName} ${bookingData?.User?.lastName}
                LOCATION:${addressText}
                END:VEVENT
                END:VCALENDAR`;

            const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.setAttribute('download', `termin_${bookingData?.User?.firstName}_${bookingData?.User?.lastName}.ics`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            notifyErrorToast('Es gab einen Fehler beim Erstellen der Kalenderdatei.', {
              title: 'Kalenderexport fehlgeschlagen',
            });
        }
    };

    const openGoogleMaps = () => {
        if (!hasExactLocation || !exactAddressText) return;
        const encodedAddress = encodeURIComponent(exactAddressText);
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodedAddress}`, '_blank');
    };

  return (
      <div className={classes.container}>
            <Link to={`/studio/${bookingData?.Studio?.id}`}>
                <div className={classes.studioInfos}>
                    <div className={classes.studioInfosInnerContainer}>
                        <div className={classes.studioImage}>
                            <img src={bookingData?.Studio?.processedImages?.[0] || noImage} alt={bookingData?.Studio?.StudioName} />
                        </div>
                        <div className={classes.studioText}>
                            <span className="mediumLarge">{bookingData?.Studio?.StudioName}</span>
                            <p className="regularXxs">{addressText || 'Der genaue Standort wird nach bestätigter Buchung mitgeteilt.'}</p>
                        </div>
                    </div>
                    {packageData && (
                        <div className={classes.packageInfoContainer}>
                            <div className={classes.packageInfoContainerImage}>
                                <img src={fromCreatorPage ? packageData?.firstPortfolioImage : packageData?.processedPortfolioImages?.[0] || noImage} alt={packageData?.name} />
                            </div>
                            <div className={classes.packageInfoContainerText}>
                                <span className="semiboldXs">{packageData?.name}</span>
                            </div>
                        </div>
                    )}
                </div>  
            </Link>
            <div className={classes.thickHorizontalLine} />
            <div className={classes.statusContainer}>
                {(() => {
                    const statusDisplay = getStatusDisplay();
                    return (
                        <>
                            {statusDisplay.message && (
                                <p style={{marginBottom: '8px', color: 'var(--dark-grey-3)'}} className="regularXxs">
                                    {statusDisplay.message}
                                </p>
                            )}
                            <div className={classes.statusTextContainer}>
                                <span className="semiboldMiddle">Status</span>
                                <span style={{color: statusDisplay.color}} className="regularXxs">
                                    {statusDisplay.statusText}
                                </span>
                            </div>
                        </>
                    );
                })()}
            </div>
            <div className={classes.thickHorizontalLine} />
            <div className={classes.bookingDetailsContainer}>
                <h2 className={`${classes.mainTitle} titleSemibold3`}>Buchungsdetails</h2>
                <h3 className="semiboldMiddle">Datum und Zeit</h3>
                <div className={classes.bookingDetails}>
                    <div className={classes.time} style={{gap: fromCreatorPage ? '16px' : '70px', flexDirection: fromCreatorPage ? 'column' : 'row'}}>
                        <div className={classes.dateTimeContainer}>
                            {bookingData?.startDateTime && bookingData?.endDateTime && (
                                <>
                                    <p className="regularLarge">{formatDateTime(bookingData?.startDateTime, bookingData?.endDateTime).date}</p>
                                    <p className="regularLarge">{formatDateTime(bookingData?.startDateTime, bookingData?.endDateTime).time}</p>
                                </>
                            )}
                        </div>
                        {bookingData?.startDateTime && bookingData?.endDateTime && (
                            <p className="regularXxs">Dauer: {calculateDuration(bookingData.startDateTime, bookingData.endDateTime)} Stunden</p>
                        )}
                    </div>
                </div>
                <button onClick={generateICSFile} className={classes.calendarButton}>
                    In Kalender exportieren
                </button>
                <div className={classes.thinHorizontalLine} />
                {!fromCreatorPage && (
                    <>
                        <h3 className={`${classes.addressTitle} semiboldMiddle`}>Adresse</h3>
                        <p className={`${classes.addressContainer} regularLarge`}>{addressText || 'Der genaue Standort wird nach bestätigter Buchung mitgeteilt.'}</p>
                        {hasExactLocation ? (
                            <button onClick={openGoogleMaps} className={classes.calendarButton}>Wegbeschreibung</button>
                        ) : null}
                        <div className={classes.thinHorizontalLine} />
                        </>
                )}
                <h3 className={`${classes.addressTitle} semiboldMiddle`}>Teilnehmer</h3>
                <p className={`${classes.addressContainer} regularLarge`}>{bookingData?.Studio?.numberOfPeople} Personen</p>
            </div>
            {fromCreatorPage && (
                <div className={classes.thickHorizontalLine} />
            )}
            {fromCreatorPage && (
                <div className={`${classes.priceContainer} ${isPriceExpanded ? classes.expanded : ''}`}>
                {isPriceExpanded && (
                    <div className={classes.priceBreakdown}>
                        <div className={classes.breakdownItem}>
                            <span className="regularLarge">
                                €{bookingData?.Studio?.pricePerHour} x {formatBookingDuration(bookingData?.startDateTime, bookingData?.endDateTime)}
                            </span>
                            <span className="regularLarge">
                                €{(calculateBookingDuration(bookingData?.startDateTime, bookingData?.endDateTime) * bookingData?.Studio?.pricePerHour).toFixed(2)}
                            </span>
                        </div>
                        {appliedFees.map((fee, index) => (
                            <div className={classes.breakdownItem} key={fee?.id || fee?.name || index}>
                                <span className="regularLarge">{fee?.name || fee?.description || 'Gebühr'}</span>
                                <span className="regularLarge">€{Number(fee?.amount || 0).toFixed(2)}</span>
                            </div>
                        ))}
                    </div>
                )}
                <div className={classes.priceMainContent}>
                    <div className={classes.priceInnerContainer}>
                        <h2 style={{marginBottom: "0px"}} className={`${classes.cancellationTitle} semiboldMiddle`}>Gesamtbetrag</h2>
                        <button onClick={togglePriceExpand} className={classes.priceDetails}>
                            {isPriceExpanded ? 'Weniger anzeigen' : 'Details sehen'}
                        </button>
                    </div>
                    <p className={`${classes.price} titleSemibold3`}>€{bookingData?.totalPrice}</p>
                </div>
                </div>
            )}
            {fromCreatorPage && (
                <div className={classes.thickHorizontalLine} />
            )}
            <div className={classes.thickHorizontalLine} />
            <BookingDetailsMain fromCreatorPage={fromCreatorPage} bookingData={bookingData} packageData={packageData} />
            <BookingDetailsCard 
                fromCreatorPage={fromCreatorPage}
                bookingData={bookingData} 
            />
      </div>
  );
};

export default BookingPhoneContent;
